import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppIcon from '@/components/AppIcon';
import { appMonthNameYear } from '@/utils/appDateFormat';
import { getENPSEmployeeBriefing } from '@/api/PreformanceManagement/ENPSAnalytics';

const WhatWeLearnedForEmployees: React.FC = () => {
  const { t, i18n } = useTranslation();

  const [points, setPoints] = useState<string[]>([]);
  const [surveyMonth, setSurveyMonth] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const result = await getENPSEmployeeBriefing();
        if (!active) return;

        if (result.errors?.length) {
          console.error('eNPS employee briefing unavailable:', result.errors[0]?.description);
          return;
        }

        const published = (result.payload?.points ?? []).filter((point) => point?.trim());
        if (published.length) {
          setPoints(published);
          setSurveyMonth(result.payload?.surveyMonth ?? null);
        }
      } catch (error) {
        // A missing HR summary is not the employee's problem, so it stays off their screen.
        console.error('eNPS employee briefing unavailable:', error);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, []);

  // Nothing published, still loading, or the fetch failed: the employee sees no container at all.
  if (!points.length) return null;

  const monthLabel = surveyMonth
    ? appMonthNameYear(surveyMonth)
    : null;

  return (
    <section
      className="border border-border rounded-lg p-4 mt-6"
      data-testid="enps-employee-briefing"
    >
      <div className="flex items-start">
        <AppIcon
          name="MessageSquareQuote"
          size={20}
          className="text-primary me-3 mt-0.5 shrink-0"
        />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-foreground">
            {t('performance.espn.whatWeLearned.title')}
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {monthLabel
              ? t('performance.espn.whatWeLearned.subtitleMonth', { month: monthLabel })
              : t('performance.espn.whatWeLearned.subtitleNoMonth')}
          </p>

          {/* A lone takeaway reads as prose; several read better as a scannable list. */}
          {points.length === 1 ? (
            <p
              className="mt-3 text-sm leading-relaxed text-foreground"
              data-testid="enps-employee-briefing-points"
            >
              {points[0]}
            </p>
          ) : (
            <ul className="mt-3 space-y-2" data-testid="enps-employee-briefing-points">
              {points.map((point, index) => (
                <li
                  key={`${index}-${point}`}
                  className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground"
                >
                  <span
                    aria-hidden="true"
                    className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70"
                  />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          )}

          <p className="mt-4 border-t border-border/60 pt-3 text-xs leading-relaxed text-muted-foreground">
            {t('performance.espn.whatWeLearned.privacy')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default WhatWeLearnedForEmployees;
